import { useAuth0 } from '@auth0/auth0-react';
import classNames from 'classnames';
import React, { useCallback, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { timeout } from '../../../../constants/app';
import { AppConfig } from '../../../../constants/config';
import { getDeviceClient } from '../../../../api/devices/client';
import { DeviceResponse } from '../../../../api/devices/types';
import {
  CompanyData,
  WeSecureWifiCredentialsInterface,
} from '../../../../api/wifi/client';
import styles from '../../WifiConnect.module.scss';
import { MemberLocation } from '../../../../api/locations/client';
import Loader from 'react-loader-spinner';
import { Select } from '../Input/Select';
import { CompanyDevicesTable } from '../Table/CompanyDevicesTable';
import { Input } from '../Input/Input';
import useAsyncEffect from 'use-async-effect';

type CompanyDevicesTabProps = {
  credentials: WeSecureWifiCredentialsInterface;
  onSuccess: Function;
  companyDevicesTabClickDetector: boolean;
  loadDevices: () => void;
  device_network_name: string;
};

type Message = {
  error?: JSX.Element;
  message?: JSX.Element;
};

export const CompanyDevicesTab: React.FC<CompanyDevicesTabProps> = ({
  credentials,
  onSuccess,
  companyDevicesTabClickDetector,
  loadDevices,
  device_network_name,
}) => {
  const [
    isErrorOnTeamDevicesLoading,
    setIsErrorOnTeamDevicesLoading,
  ] = useState<boolean>(false);
  const [formMessage, setFormMessage] = useState<Message>({});
  const [counter, setCounter] = useState(0);
  // we need this changes for a sending 'locations' and 'devices' tab only when we click on the device tab
  const [tableMessage, setTableMessage] = useState<Message>({});
  const [teamDevices, setTeamDevices] = useState<DeviceResponse[]>([]);
  const [locations, setLocations] = useState<MemberLocation[]>([]);
  const [timer, setTimer] = useState<number>();
  const [currentCompany, setCurrentCompany] = useState<string>('');
  const [isTeamDevicesLoading, setIsTeamDevicesLoading] = useState<boolean>(
    false,
  );
  const [loadCompanyDeviceCounter, setloadCompanyDeviceCounter] = useState<
    number
  >(0);

  const { getAccessTokenSilently } = useAuth0();
  const intl = useIntl();
  const ids = new Map<string, string>();

  const getCompanies = (data: CompanyData[]): string[] => {
    const companies = new Array<string>();
    for (let index in data) {
      companies.push(data[index].company_name);
      ids.set(data[index].company_name, data[index].company_id);
    }

    return companies;
  };

  useAsyncEffect(async () => {
    // we need this changes for a sending 'locations' and 'devices' tab only when we click on the device tab
    if (counter !== 0) {
      setCurrentCompany(credentials.company_data[0].company_name);
    }
    setCounter((oldValue) => oldValue + 1);
  }, [companyDevicesTabClickDetector]);

  const loadTeamDevices = async () => {
    const token = await getAccessTokenSilently();
    const pageSize = 100;

    let devices = [];
    let isStart = true;
    let newDevices: DeviceResponse[] = [];
    let nextOffset = 0;

    while (isStart || newDevices.length === pageSize) {
      const resp = await getDeviceClient(
        AppConfig.appEnv,
        token,
      ).getListOfDevicesByCompanyID(
        pageSize,
        nextOffset,
        ids.get(currentCompany)!,
      );

      if ('devices' in resp) {
        devices.push(...resp.devices);
        newDevices = resp.devices;
        nextOffset = resp.next_offset;
        isStart = false;
      } else {
        if ('status' in resp) {
          setIsErrorOnTeamDevicesLoading(true);
          setTimeout(() => {
            setIsErrorOnTeamDevicesLoading(false);
          }, 5000);
        }
        isStart = false;
        break;
      }
    }

    setTeamDevices(devices);
    setIsTeamDevicesLoading(false);
  };

  const setMessage = useCallback(
    (
      msg: JSX.Element,
      type: string,
      stateFunc: React.Dispatch<React.SetStateAction<Message>>,
    ) => {
      setFormMessage({});
      setTableMessage({});
      stateFunc({ [type]: msg });

      clearTimeout(timer);
      setTimer(
        (setTimeout(
          () => stateFunc((prev) => ({ ...prev, [type]: undefined })),
          timeout,
        ) as unknown) as number,
      );
    },
    [timer],
  );

  return (
    <div
      className={classNames(
        'ray-grid__cell--span-4 ray-grid__cell--push-4-desktop ray-grid__cell--push-1-tablet ray-grid__cell--span-6-tablet ray-grid__cell--span-12-phone',
        styles.tabContent,
      )}
    >
      <div className={styles.textFieldWrapper}>
        <div className="ray-grid__cell--span-full" style={{ marginBottom: 10 }}>
          <span>
            <FormattedMessage
              id={'tabs.companyDevices.instructions.tabs.0'}
              defaultMessage="Here you can see all the devices added by your company to use {network_name} (including your own)"
              values={{ network_name: device_network_name }}
            />
          </span>
          <br />

          <ul className={styles.corpIoTInstructionsList}>
            <li>
              <span>
                <FormattedMessage
                  id="tabs.companyDevices.instructions.tabs.1"
                  defaultMessage="Select your company"
                />
              </span>
            </li>
            <li>
              <span>
                <FormattedMessage
                  id="tabs.companyDevices.instructions.tabs.2"
                  defaultMessage="Click 'Load devices for company'"
                />
              </span>
            </li>
          </ul>
          <span>
            <FormattedMessage
              id="tabs.companyDevices.instructions.tabs.3"
              defaultMessage="If you want to take ownership of a device added by an inactive owner..."
            />
          </span>
          <ul className={styles.corpIoTInstructionsList}>
            <li>
              <span>
                <FormattedMessage
                  id="tabs.companyDevices.instructions.tabs.4"
                  defaultMessage="Click 'Take ownership' next to the device and then click 'OK'"
                />
              </span>
            </li>
          </ul>
          <span>
            <FormattedMessage
              id="tabs.companyDevices.instructions.tabs.5"
              defaultMessage="This will change ownership of the device to yourself and will now appear in your list of devices."
            />
          </span>
          <br />
          <span>
            <FormattedMessage
              id="tabs.companyDevices.instructions.tabs.6"
              defaultMessage="If you do not see this option next to a device, the current owner account is still active."
            />
          </span>
        </div>
        <br />
        {getCompanies(credentials.company_data).length === 1 ? (
          <Input
            id={'user-companies'}
            text={currentCompany}
            labelText={
              <FormattedMessage
                id="tabs.companyDevices.input.label"
                defaultMessage="Your companies"
              />
            }
            placeholder={currentCompany}
            isDisabled={true}
          ></Input>
        ) : (
          <Select
            label={
              <label className="ray-select__label">
                <FormattedMessage
                  id="tabs.companyDevices.select.label"
                  defaultMessage="Your companies"
                />
              </label>
            }
            currentItem={currentCompany}
            items={getCompanies(credentials.company_data)}
            mapper={(company: string) => (
              <option key={company} value={company}>
                {company}
              </option>
            )}
            onSet={(company: string) => {
              setCurrentCompany(company);
            }}
            onDeviceTypeSelect={(deviceType: string) => {}}
          />
        )}
        <div
          className={styles.teamDevicesButtonsContainer}
          style={{ marginTop: 20 }}
        >
          <button
            className={classNames(
              'ray-button ray-button--primary ray-button--compact',
              styles.button,
            )}
            onClick={() => {
              setIsTeamDevicesLoading(true);
              loadTeamDevices();

              setloadCompanyDeviceCounter((prevState) => prevState + 1);
            }}
            disabled={!currentCompany}
          >
            <FormattedMessage
              id="tabs.companyDevices.actions.load"
              defaultMessage={'Load devices for company'}
            />
          </button>
          <button
            className={classNames(
              'ray-button ray-button--secondary ray-button--compact',
              styles.button,
            )}
            onClick={() => {
              setTeamDevices([]);
              setloadCompanyDeviceCounter(0);
            }}
            disabled={!currentCompany}
          >
            <FormattedMessage
              id="tabs.companyDevices.actions.clear"
              defaultMessage={'Clear'}
            />
          </button>
        </div>

        {isErrorOnTeamDevicesLoading ? (
          <div className={classNames(styles.notification, styles.errorMsg)}>
            <FormattedMessage
              id={`tabs.device.messages.errors.internal_error`}
              defaultMessage="Internal error. Check your Internet connection and try again"
            />
          </div>
        ) : (
          <></>
        )}

        {isTeamDevicesLoading ? (
          <div className={styles.formLoader} style={{ marginTop: 20 }}>
            <Loader type="Oval" color="#00F" height={40} width={40} />
          </div>
        ) : teamDevices.length === 0 && loadCompanyDeviceCounter !== 0 ? (
          <div
            className={classNames(
              'ray-grid__cell--span-full',
              styles.textFieldWrapper,
            )}
          >
            <div className={styles.notification}>
              <FormattedMessage
                id="tabs.companyDevices.info.noDevices"
                defaultMessage="No devices found for this company."
              />
            </div>
          </div>
        ) : teamDevices.length === 0 && loadCompanyDeviceCounter === 0 ? (
          <></>
        ) : (
          <div className="ray-grid__cell--span-full" style={{ marginTop: 20 }}>
            {tableMessage.message}
            <CompanyDevicesTable
              devices={teamDevices}
              locations={locations}
              onUpdate={() => {
                loadTeamDevices();
                loadDevices();
                onSuccess();
              }}
              onMessage={(msg: JSX.Element) =>
                setMessage(msg, 'message', setTableMessage)
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};
